import { Component, DestroyRef, effect, inject, signal, untracked } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { AnalyticsService } from '../../core/services/analytics.service';
import { DateRangeService } from '../../core/services/date-range.service';
import { PagedResult } from '../../core/models/common.models';
import { DeveloperIntelligenceListItem, DeveloperIntelligenceListParams } from '../../core/models/developer.models';
import { LocalDatePipe } from '../../shared/pipes/local-date.pipe';

@Component({
  selector: 'app-developers-page',
  standalone: true,
  imports: [DecimalPipe, FormsModule, LocalDatePipe],
  templateUrl: './developers-page.component.html',
  styleUrl: './developers-page.component.scss',
})
export class DevelopersPageComponent {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly dateRange = inject(DateRangeService);
  private readonly destroyRef = inject(DestroyRef);
  private currentLoadId = 0;

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly result = signal<PagedResult<DeveloperIntelligenceListItem> | null>(null);
  readonly search = signal('');
  readonly sortBy = signal<DeveloperIntelligenceListParams['sortBy']>('totalIssues');
  readonly sortDirection = signal<'asc' | 'desc'>('desc');

  constructor() {
    effect(() => {
      this.dateRange.appliedRange();
      this.dateRange.appliedRevision();
      untracked(() => this.load());
    });
  }

  load(): void {
    const loadId = ++this.currentLoadId;
    this.loading.set(true);
    this.error.set(null);

    this.analyticsService
      .getDeveloperIntelligence({
        ...this.dateRange.toQueryParams(),
        search: this.search() || undefined,
        sortBy: this.sortBy(),
        sortDirection: this.sortDirection(),
        page: 1,
        pageSize: 50,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (loadId === this.currentLoadId) {
            this.loading.set(false);
          }
        }),
      )
      .subscribe({
        next: (response) => {
          if (loadId !== this.currentLoadId) return;
          this.result.set(response);
        },
        error: (err: unknown) => {
          if (loadId !== this.currentLoadId) return;
          console.error('Developer intelligence request failed', err);
          this.error.set(this.formatError(err, 'Developer intelligence could not be loaded.'));
        },
      });
  }

  applySearch(): void {
    this.load();
  }

  sortBy$(col: DeveloperIntelligenceListParams['sortBy']): void {
    if (this.sortBy() === col) {
      this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortBy.set(col);
      this.sortDirection.set('desc');
    }
    this.load();
  }

  trendIcon(direction: string): string {
    if (direction === 'improving') return '↓';
    if (direction === 'worsening') return '↑';
    return '→';
  }

  trendClass(direction: string): string {
    if (direction === 'improving') return 'trend-good';
    if (direction === 'worsening') return 'trend-bad';
    return 'trend-neutral';
  }

  trendLabel(trend: { direction: string; deltaPercent: number | null; displayLabel?: string }): string | null {
    if (trend.displayLabel) return trend.displayLabel;
    if (trend.deltaPercent === null) return null;
    return null;
  }

  hasTrendPercent(trend: { deltaPercent: number | null; displayLabel?: string }): boolean {
    return !trend.displayLabel && trend.deltaPercent !== null;
  }

  private formatError(error: unknown, fallback: string): string {
    if ((error as { name?: string })?.name === 'TimeoutError') {
      return 'Request timed out after 8s.';
    }
    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) return `Cannot reach backend${error.url ? ` @ ${error.url}` : ''}.`;
      const details = typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
      return `${fallback} (${error.status}: ${details || error.message})`;
    }
    return fallback;
  }
}
